import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useDemoData } from '../components/useDemoData';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  Search, DollarSign, Users, AlertTriangle, XCircle,
  CheckCircle2, ExternalLink, TrendingUp, CreditCard,
  ShieldAlert, Eye, Trash2, RefreshCw, Package
} from 'lucide-react';
import { toast } from 'sonner';
import EmptyDataState from '../components/EmptyDataState';

const DEMO_SAAS = {
  summary: {
    totalApps: 94,
    totalSpend: 28450,
    unusedApps: 17,
    duplicateApps: 6,
    wastedSpend: 8740,
    avgPerUser: 342,
    licenses: 487,
    unusedLicenses: 112,
  },
  apps: [
    { id: 1, name: 'Datadog', category: 'Monitoring', monthlySpend: 4200, licenses: 45, activeUsers: 38, utilization: 84, status: 'active', lastLogin: '2 hours ago', risk: 'none' },
    { id: 2, name: 'Jira', category: 'Project Mgmt', monthlySpend: 2100, licenses: 120, activeUsers: 95, utilization: 79, status: 'active', lastLogin: '1 hour ago', risk: 'none' },
    { id: 3, name: 'PagerDuty', category: 'Incident Mgmt', monthlySpend: 1800, licenses: 30, activeUsers: 28, utilization: 93, status: 'active', lastLogin: '4 hours ago', risk: 'none' },
    { id: 4, name: 'New Relic', category: 'Monitoring', monthlySpend: 3600, licenses: 40, activeUsers: 12, utilization: 30, status: 'underused', lastLogin: '3 weeks ago', risk: 'duplicate', duplicate: 'Datadog' },
    { id: 5, name: 'Monday.com', category: 'Project Mgmt', monthlySpend: 890, licenses: 35, activeUsers: 8, utilization: 23, status: 'underused', lastLogin: '6 weeks ago', risk: 'duplicate', duplicate: 'Jira' },
    { id: 6, name: 'Asana', category: 'Project Mgmt', monthlySpend: 720, licenses: 25, activeUsers: 3, utilization: 12, status: 'unused', lastLogin: '4 months ago', risk: 'duplicate', duplicate: 'Jira' },
    { id: 7, name: 'Splunk', category: 'Monitoring', monthlySpend: 5200, licenses: 20, activeUsers: 6, utilization: 30, status: 'underused', lastLogin: '2 weeks ago', risk: 'duplicate', duplicate: 'Datadog' },
    { id: 8, name: 'Looker', category: 'Analytics', monthlySpend: 2400, licenses: 50, activeUsers: 44, utilization: 88, status: 'active', lastLogin: '30 min ago', risk: 'none' },
    { id: 9, name: 'Mixpanel', category: 'Analytics', monthlySpend: 1200, licenses: 30, activeUsers: 4, utilization: 13, status: 'unused', lastLogin: '3 months ago', risk: 'duplicate', duplicate: 'Looker' },
    { id: 10, name: 'Confluence', category: 'Documentation', monthlySpend: 780, licenses: 120, activeUsers: 62, utilization: 52, status: 'active', lastLogin: '1 hour ago', risk: 'none' },
    { id: 11, name: 'Notion', category: 'Documentation', monthlySpend: 640, licenses: 45, activeUsers: 18, utilization: 40, status: 'underused', lastLogin: '1 week ago', risk: 'duplicate', duplicate: 'Confluence' },
    { id: 12, name: 'Figma', category: 'Design', monthlySpend: 540, licenses: 15, activeUsers: 14, utilization: 93, status: 'active', lastLogin: '2 hours ago', risk: 'none' },
    { id: 13, name: 'InVision', category: 'Design', monthlySpend: 380, licenses: 15, activeUsers: 0, utilization: 0, status: 'unused', lastLogin: 'Never', risk: 'zombie' },
    { id: 14, name: 'Segment', category: 'Data', monthlySpend: 1800, licenses: 10, activeUsers: 8, utilization: 80, status: 'active', lastLogin: '6 hours ago', risk: 'none' },
    { id: 15, name: 'CircleCI', category: 'CI/CD', monthlySpend: 920, licenses: 30, activeUsers: 22, utilization: 73, status: 'active', lastLogin: '1 hour ago', risk: 'none' },
    { id: 16, name: 'Travis CI', category: 'CI/CD', monthlySpend: 340, licenses: 20, activeUsers: 0, utilization: 0, status: 'unused', lastLogin: 'Never', risk: 'zombie' },
    { id: 17, name: 'Heroku', category: 'Platform', monthlySpend: 440, licenses: 8, activeUsers: 0, utilization: 0, status: 'unused', lastLogin: '8 months ago', risk: 'zombie' },
  ],
  categories: [
    { name: 'Monitoring', count: 3, spend: 13000, waste: 5200 },
    { name: 'Project Management', count: 3, spend: 3710, waste: 1610 },
    { name: 'Analytics', count: 2, spend: 3600, waste: 1200 },
    { name: 'Documentation', count: 2, spend: 1420, waste: 640 },
    { name: 'Design', count: 2, spend: 920, waste: 380 },
    { name: 'CI/CD', count: 2, spend: 1260, waste: 340 },
  ],
};

const SaaSDetective = () => {
  const { shouldShowData } = useDemoData();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    // Iter84ak — real backend; fall back to rich local demo.
    let cancelled = false;
    (async () => {
      if (!shouldShowData) { setLoading(false); return; }
      try {
        const token = localStorage.getItem('token');
        await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/finops/saas-detective/audit`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!cancelled) { setData(DEMO_SAAS); setLoading(false); }
      } catch (_e) {
        if (!cancelled) { setData(DEMO_SAAS); setLoading(false); }
      }
    })();
    return () => { cancelled = true; };
  }, [shouldShowData]);

  if (!shouldShowData) {
    return <EmptyDataState title="SaaS Spend Detective" description="Find hidden subscriptions, duplicate tools, and unused licenses across your organization. Connect SSO to auto-detect." icon={Search} />;
  }

  if (loading) return <div className="p-6 flex items-center justify-center min-h-[400px]"><div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" /></div>;
  if (!data) return null;

  const filteredApps = filter === 'all' ? data.apps : data.apps.filter(a =>
    filter === 'unused' ? a.status === 'unused' : filter === 'duplicate' ? a.risk === 'duplicate' : filter === 'underused' ? a.status === 'underused' : true
  );

  const statusColor = (s) => ({ active: 'bg-emerald-500/10 text-emerald-600', underused: 'bg-amber-500/10 text-amber-600', unused: 'bg-red-500/10 text-red-600' }[s]);
  const riskBadge = (r, dup) => {
    if (r === 'duplicate') return <Badge className="bg-orange-500/10 text-orange-600 border-orange-500/30 text-[11px]">Duplicate of {dup}</Badge>;
    if (r === 'zombie') return <Badge className="bg-red-500/10 text-red-600 border-red-500/30 text-[11px]">Zombie</Badge>;
    return null;
  };

  return (
    <div className="min-h-screen bg-white text-slate-900 -mt-px">
    <div className="p-6 space-y-6 max-w-7xl mx-auto" data-testid="saas-detective-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-['Manrope'] text-slate-900 flex items-center gap-2">
            <Search className="w-7 h-7 text-emerald-400" />
            SaaS Spend <span className="text-emerald-400">Detective</span>
          </h1>
          <p className="text-slate-700 text-sm mt-1">Find hidden subscriptions and duplicate tools eating your budget.</p>
        </div>
        <Badge className="bg-red-500/10 text-red-600 border-red-500/30 text-sm px-3 py-1">
          <DollarSign className="w-3.5 h-3.5 mr-1" />${data.summary.wastedSpend.toLocaleString()}/mo wasted
        </Badge>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card><CardContent className="p-4">
          <p className="text-xs text-slate-400 mb-1">Total SaaS Apps</p>
          <p className="text-2xl font-bold text-slate-900">{data.summary.totalApps}</p>
          <p className="text-xs text-slate-400">detected across org</p>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <p className="text-xs text-slate-400 mb-1">Monthly SaaS Spend</p>
          <p className="text-2xl font-bold text-slate-900">${data.summary.totalSpend.toLocaleString()}</p>
          <p className="text-xs text-slate-400">${data.summary.avgPerUser}/user avg</p>
        </CardContent></Card>
        <Card className="border-red-200"><CardContent className="p-4">
          <p className="text-xs text-red-500 mb-1">Unused Apps</p>
          <p className="text-2xl font-bold text-red-600">{data.summary.unusedApps}</p>
          <p className="text-xs text-red-400">zero active users</p>
        </CardContent></Card>
        <Card className="border-orange-200"><CardContent className="p-4">
          <p className="text-xs text-orange-500 mb-1">Duplicate Tools</p>
          <p className="text-2xl font-bold text-orange-600">{data.summary.duplicateApps}</p>
          <p className="text-xs text-orange-400">same category overlap</p>
        </CardContent></Card>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2">
        {[
          { key: 'all', label: `All (${data.apps.length})` },
          { key: 'unused', label: `Unused (${data.apps.filter(a => a.status === 'unused').length})` },
          { key: 'duplicate', label: `Duplicates (${data.apps.filter(a => a.risk === 'duplicate').length})` },
          { key: 'underused', label: `Underused (${data.apps.filter(a => a.status === 'underused').length})` },
        ].map(f => (
          <Button key={f.key} variant={filter === f.key ? 'default' : 'outline'} size="sm"
            className={filter === f.key ? 'bg-emerald-500 hover:bg-blue-600' : ''} onClick={() => setFilter(f.key)}>
            {f.label}
          </Button>
        ))}
      </div>

      {/* App List */}
      <Card>
        <CardContent className="p-0">
          <div className="divide-y divide-slate-100">
            {filteredApps.map(app => (
              <div key={app.id} className="flex items-center gap-4 p-4 hover:bg-slate-50 transition-colors" data-testid={`saas-app-${app.id}`}>
                <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                  <Package className="w-5 h-5 text-slate-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-slate-800">{app.name}</p>
                    <Badge variant="outline" className="text-[11px]">{app.category}</Badge>
                    {riskBadge(app.risk, app.duplicate)}
                  </div>
                  <p className="text-xs text-slate-400">Last login: {app.lastLogin} • {app.activeUsers}/{app.licenses} users active</p>
                </div>
                <div className="w-24">
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="text-slate-400">Usage</span>
                    <span className={app.utilization < 30 ? 'text-red-500 font-medium' : 'text-slate-400'}>{app.utilization}%</span>
                  </div>
                  <Progress value={app.utilization} className="h-1.5" />
                </div>
                <div className="text-right w-24">
                  <p className="text-sm font-mono font-bold text-slate-800">${app.monthlySpend.toLocaleString()}</p>
                  <Badge className={`${statusColor(app.status)} text-[11px] border-0`}>{app.status}</Badge>
                </div>
                <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8" onClick={() => toast.success(`${app.name} flagged for review`)}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
    </div>
  );
};

export default SaaSDetective;
